import { colors } from "../styles/colors";
import { Link } from "react-router-dom";
import { ArrowRight, Home } from "lucide-react";
import SponsorPlaceholder from "../components/SponsorPlaceholder";
import ScrollToTop from "../components/ScrollToTop";

export default function CancellationClause() {
  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.background }}>
      <header className="px-6 md:px-12 lg:px-24 py-6 " style={{ borderColor: colors.border }}>
        <Link to="/" data-testid="home-link" className="inline-flex items-center gap-2 text-lg font-medium hover:opacity-80 transition-opacity" style={{ color: colors.accent }}>
          <img src="/clauseclear-logo.png" alt="ClauseClear" className="h-10 object-contain" />

        </Link>
      </header>

      <div className="h-32 md:h-48" style={{ background: `linear-gradient(135deg, ${colors.backgroundDark} 0%, ${colors.background} 50%, ${colors.backgroundLight} 100%)` }} />

      <article className="px-6 md:px-12 lg:px-24 py-16">
        <div className="max-w-4xl mx-auto">
          <h1 data-testid="page-title" className="text-4xl md:text-5xl font-light tracking-tight leading-[1.1] mb-8" style={{ color: colors.text }}>
            What Does a Cancellation Clause Mean?
          </h1>

          <div className="prose prose-lg max-w-none">
            <section className="mb-12">
              <h2 className="text-2xl font-medium mb-4" style={{ color: colors.accent }}>Plain-English Explanation</h2>
              <p className="text-base md:text-lg leading-relaxed mb-4" style={{ color: colors.textMuted }}>
                A cancellation clause explains how and when either party can end a service agreement, subscription, or ongoing contract before its natural completion. It typically outlines the process for cancellation, any required notice period, and whether refunds or penalties apply.
              </p>
              <p className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                Unlike termination clauses in employment contracts, cancellation clauses usually appear in service agreements, subscriptions, event bookings, or purchase contracts. They define your exit options and the associated costs or procedures.
              </p>
            </section>

            <section className="mb-12 rounded-2xl p-8" style={{ backgroundColor: colors.backgroundLight, border: `1px solid ${colors.border}` }} style={{ borderColor: colors.border }}>
              <h2 className="text-2xl font-medium mb-4" style={{ color: colors.accent }}>Example Clause</h2>
              <div className="p-6 rounded-xl text-base" style={{ backgroundColor: colors.backgroundDark, color: colors.text, fontFamily: 'monospace' }}>
                "Client may cancel this service agreement at any time by providing written notice to Provider. Cancellations made with more than 30 days' notice will receive a full refund of any advance payments. Cancellations made with 15-30 days' notice will receive a 50% refund. Cancellations made with less than 15 days' notice are non-refundable. Any work completed prior to cancellation remains payable at the agreed rate."
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-medium mb-4" style={{ color: colors.accent }}>Why This Clause Is Used</h2>
              <p className="text-base md:text-lg leading-relaxed mb-4" style={{ color: colors.textMuted }}>
                Service providers and businesses include cancellation clauses to manage planning and financial risk:
              </p>
              <ul className="space-y-3 ml-6">
                <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                  To establish clear exit procedures that both parties understand
                </li>
                <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                  To compensate for lost revenue or wasted preparation when clients cancel
                </li>
                <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                  To give providers time to fill cancelled slots or adjust resources
                </li>
                <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                  To distinguish between early cancellation and scheduled completion
                </li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-medium mb-4" style={{ color: colors.accent }}>What This Usually Affects</h2>
              <ul className="space-y-3 ml-6">
                <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                  <strong>Refund eligibility:</strong> Whether you get money back depends on timing and terms
                </li>
                <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                  <strong>Cancellation fees:</strong> You may owe penalties or forfeit deposits
                </li>
                <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                  <strong>Notice requirements:</strong> How and when you must communicate your decision
                </li>
                <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                  <strong>Partial performance:</strong> What happens to work already completed
                </li>
              </ul>
            </section>

            <section className="mb-12 rounded-2xl p-8" style={{ backgroundColor: colors.backgroundLight, border: `1px solid ${colors.border}` }} style={{ borderColor: colors.border }}>
              <h2 className="text-2xl font-medium mb-4" style={{ color: colors.accent }}>Important Context</h2>
              <p className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                Consumer protection laws in many jurisdictions provide minimum cancellation rights that may override restrictive contract terms. Some types of purchases (like online orders or gym memberships) may have legally mandated "cooling off" periods. Cancellation terms that are found to be unfair or unconscionable may not be enforceable. This explanation covers typical contract language but doesn't constitute legal advice about your specific cancellation rights or obligations.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-medium mb-6" style={{ color: colors.accent }}>Related Clauses</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Link to="/explain-notice-period-clause" data-testid="related-link-notice" className="p-6 rounded-xl hover:shadow-md transition-all" style={{ borderColor: colors.border }}>
                  <h3 className="font-medium mb-2" style={{ color: colors.accent }}>Notice Period</h3>
                  <p className="text-sm" style={{ color: colors.textMuted }}>Understand advance warning requirements</p>
                </Link>
                <Link to="/explain-indemnity-clause" data-testid="related-link-indemnity" className="p-6 rounded-xl hover:shadow-md transition-all" style={{ borderColor: colors.border }}>
                  <h3 className="font-medium mb-2" style={{ color: colors.accent }}>Indemnity Clause</h3>
                  <p className="text-sm" style={{ color: colors.textMuted }}>Learn about liability protection</p>
                </Link>
              </div>
            </section>

            <div data-testid="cta-section" className="text-center p-8 rounded-2xl" style={{ borderColor: colors.border }}>
              <p className="text-lg mb-4" style={{ color: colors.textMuted }}>Want to understand your own clause?</p>
              <Link to="/" data-testid="cta-button" className="inline-flex items-center gap-2 text-white hover:opacity-90 rounded-full px-8 py-4 text-lg font-medium transition-all" style={{ backgroundColor: '#2F3E30' }}>
                Try ClauseClear
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>

            {/* Sponsorship Placeholder */}
            <SponsorPlaceholder variant="full" />
          </div>
        </div>
      </article>
    </div>
  );
}
